import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import api from '../../../api/axiosInstance';
import {
  StageFailureDetail,
  StageFailureDetailsResponse,
  StageFailureDetailsParams,
  StageFailureSortBy,
  StageFailureSortOrder
} from '../../../const/StageFailureTypes';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { toStartOfDayISO, toEndOfDayISO } from '../utils/dashboardDateUtils';

export interface UseStageFailureDetailsParams {
  startDate: string;
  endDate: string;
  pageIndex: number;
  pageSize: number;
  sortBy?: StageFailureSortBy;
  sortOrder?: StageFailureSortOrder;
  filters?: {
    stage?: string;
    workflowId?: string;
    workflowName?: string;
    stepName?: string;
    environment?: string;
    errorSummary?: string;
  };
}

function buildQueryString(params: StageFailureDetailsParams): string {
  const startISO = toStartOfDayISO(params.startDate);
  const endISO = toEndOfDayISO(params.endDate);
  const search = new URLSearchParams();
  search.set('startDate', startISO);
  search.set('endDate', endISO);

  if (params.limit != null) search.set('limit', String(params.limit));
  if (params.offset != null) search.set('offset', String(params.offset));
  if (params.sortBy) search.set('sortBy', params.sortBy);
  if (params.sortOrder) search.set('sortOrder', params.sortOrder);

  const addFilter = (key: keyof StageFailureDetailsParams, value: string | undefined) => {
    if (value != null && String(value).trim() !== '') search.set(key, String(value).trim());
  };
  addFilter('stage', params.stage);
  addFilter('workflowId', params.workflowId);
  addFilter('workflowName', params.workflowName);
  addFilter('stepName', params.stepName);
  addFilter('environment', params.environment);
  addFilter('errorSummary', params.errorSummary);

  return search.toString();
}

export async function fetchStageFailureDetails(params: StageFailureDetailsParams): Promise<StageFailureDetailsResponse> {
  const qs = buildQueryString(params);
  const { data } = await api.get<StageFailureDetailsResponse>(`/api/dashboard-metric/stage-failure-details?${qs}`);
  return data;
}

export function useStageFailureDetails({
  startDate,
  endDate,
  pageIndex,
  pageSize,
  sortBy,
  sortOrder,
  filters = {}
}: UseStageFailureDetailsParams): {
  data: StageFailureDetail[];
  total: number;
  limit: number;
  offset: number;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
} {
  const { addNotifications } = useSwobNotifications();
  const [hasError, setHasError] = useState(false);
  const endDateIsToday = endDate === new Date().toISOString().slice(0, 10);

  const params: StageFailureDetailsParams = {
    startDate,
    endDate,
    limit: pageSize,
    offset: pageIndex * pageSize,
    sortBy: sortBy ?? 'step_timestamp',
    sortOrder: sortOrder ?? 'DESC',
    ...(filters.stage != null && filters.stage.trim() !== '' && { stage: filters.stage }),
    ...(filters.workflowId != null && filters.workflowId.trim() !== '' && { workflowId: filters.workflowId }),
    ...(filters.workflowName != null && filters.workflowName.trim() !== '' && { workflowName: filters.workflowName }),
    ...(filters.stepName != null && filters.stepName.trim() !== '' && { stepName: filters.stepName }),
    ...(filters.environment != null && filters.environment.trim() !== '' && { environment: filters.environment }),
    ...(filters.errorSummary != null && filters.errorSummary.trim() !== '' && { errorSummary: filters.errorSummary })
  };

  const query = useQuery({
    queryKey: [
      'stage-failure-details',
      startDate,
      endDate,
      pageIndex,
      pageSize,
      params.sortBy,
      params.sortOrder,
      filters.stage,
      filters.workflowId,
      filters.workflowName,
      filters.stepName,
      filters.environment,
      filters.errorSummary
    ],
    queryFn: () => fetchStageFailureDetails(params),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(startDate && endDate),
    refetchInterval: !hasError && endDateIsToday ? 60000 : false,
    placeholderData: keepPreviousData
  });

  useEffect(() => {
    if (query.error && !hasError) {
      setHasError(true);
      addNotifications([
        {
          title: 'Error - Failed SW Delivery',
          icon: 'icon-triangle-warning',
          description: 'An error occurred while fetching stage failure details.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  }, [query.error, hasError, addNotifications]);

  const errorMessage = query.error instanceof Error ? query.error.message : query.error ? String(query.error) : null;

  return {
    data: query.data?.details ?? [],
    total: query.data?.total ?? 0,
    limit: query.data?.limit ?? pageSize,
    offset: query.data?.offset ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? errorMessage : null
  };
}
